'use es6';

import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
import _classPrivateFieldLooseBase from "@babel/runtime/helpers/esm/classPrivateFieldLooseBase";
import _classPrivateFieldLooseKey from "@babel/runtime/helpers/esm/classPrivateFieldLooseKey";
const _excluded = ["action", "_callbackId", "args"];
import Interframe from 'interframe';
import Raven from 'raven-js';
import Backbone from 'Backbone';
import { INPAGE_REDUX_ACTION, PARENT_APP_MESSAGING_READY, UPLOAD_FILE, LBUI_I18N_INIT, INPAGE_APP_INITIALIZATION_STARTED } from 'EmailEditorUI/dragDropMessageTypes';
import { markElementAsSelected, markElementAsSelectedWithSmartRule } from 'LayoutBuilderUI/actions/layoutActions';
import { appendNewElementToLastSection, selectElementByModulePath } from 'EmailEditorUI/redux/actions/LayoutActions';
import { updateSidebarTab } from 'EmailEditorUI/redux/actions/sidebarActions';
import { AI_DYNAMIC_TOKEN_INSERT } from 'EmailEditorUI/AIDynamicText/types';
import { EVENT_REACT_DND_TEARDOWN_START } from 'ContentEditorUI/lib/inpage/Constants';
import { startI18n } from 'LayoutBuilderUI/utils/I18nUtils';
import { reportRenderFailed } from 'LayoutBuilderUI/actions/ApiActions';
var _isAppReady = /*#__PURE__*/_classPrivateFieldLooseKey("isAppReady");
var _onAppReadySubscribers = /*#__PURE__*/_classPrivateFieldLooseKey("onAppReadySubscribers");
var _i18nInitializedPromise = /*#__PURE__*/_classPrivateFieldLooseKey("i18nInitializedPromise");
class Messenger {
  /** @type {Array<VoidFunction>} */

  /** @type {Promise<unknown>} */

  constructor() {
    Object.defineProperty(this, _isAppReady, {
      writable: true,
      value: false
    });
    Object.defineProperty(this, _onAppReadySubscribers, {
      writable: true,
      value: []
    });
    Object.defineProperty(this, _i18nInitializedPromise, {
      writable: true,
      value: void 0
    });
    this.setStore = store => {
      this.store = store;
    };
    this.dispatchAction = action => {
      this.postMessage(INPAGE_REDUX_ACTION, action);
    };
    this.handleAppReady = () => {
      _classPrivateFieldLooseBase(this, _isAppReady)[_isAppReady] = true;
      _classPrivateFieldLooseBase(this, _onAppReadySubscribers)[_onAppReadySubscribers].forEach(callback => callback());
      _classPrivateFieldLooseBase(this, _onAppReadySubscribers)[_onAppReadySubscribers] = [];
    };
    this.receiveMessage = message => {
      if (!message) {
        console.warn('Empty LBUI action');
        return;
      }
      switch (message.action) {
        case 'uploadFileEmailDndProgress':
          this.trigger('uploadFileEmailDndProgress', message);
          break;
        case PARENT_APP_MESSAGING_READY:
          {
            this.handleAppReady();
            break;
          }
        case LBUI_I18N_INIT:
          {
            _classPrivateFieldLooseBase(this, _i18nInitializedPromise)[_i18nInitializedPromise] = startI18n(message);
            break;
          }
        case AI_DYNAMIC_TOKEN_INSERT:
          {
            if (this.onAIDynamicTokenInsert) {
              this.onAIDynamicTokenInsert(message);
            }
            break;
          }
        case 'previewNeedsReload':
          {
            this.handlePreviewNeedsReload();
            break;
          }
        case 'reduxAction':
          {
            this.trigger('reduxAction', message);
            const {
                args = []
              } = message,
              reduxAction = _objectWithoutPropertiesLoose(message, _excluded);
            const layoutBuilderActions = {
              markElementAsSelected,
              markElementAsSelectedWithSmartRule,
              appendNewElementToLastSection,
              selectElementByModulePath,
              updateSidebarTab
            };
            const action = layoutBuilderActions[reduxAction.type] ? layoutBuilderActions[reduxAction.type](...args) : reduxAction;
            if (this.store) {
              try {
                this.store.dispatch(action);
              } catch (e) {
                Raven.captureBreadcrumb({
                  message: e.message,
                  data: reduxAction,
                  level: 'error'
                });
                throw e;
              }
            }
            break;
          }
        case 'insertIntoRichText':
          {
            if (this.onInsertRichText) {
              this.onInsertRichText(message);
            }
            break;
          }
        default:
          try {
            if (localStorage.INPAGE_MESSENGER_DEBUG) {
              console.log('Unhandled LBUI action:', message.action, message);
            }
          } catch (e) {
            return;
          }
      }
    };
    Object.assign(this, Backbone.Events);
    if (window.jasmine) return;
    this.messenger = new Interframe(window.hsVars.app_hs_base_url, window.parent);
    this.messenger.subscribe(this.receiveMessage);
  }
  onAppReady(callback) {
    if (_classPrivateFieldLooseBase(this, _isAppReady)[_isAppReady]) {
      callback();
    } else {
      _classPrivateFieldLooseBase(this, _onAppReadySubscribers)[_onAppReadySubscribers].push(callback);
      // If the parent app was ready before LBUI, we will immediately report ready to the parent
      this.postMessage(INPAGE_APP_INITIALIZATION_STARTED, null, this.handleAppReady);
    }
  }
  onI18nReady(callback) {
    _classPrivateFieldLooseBase(this, _i18nInitializedPromise)[_i18nInitializedPromise].then(callback).catch(err => {
      reportRenderFailed();
      Raven.captureException(new Error('I18n failed to initialize', {
        cause: err
      }));
    });
  }
  setOnInsertRichText(callback) {
    this.onInsertRichText = callback;
  }
  setOnAIDynamicTokenInsert(callback) {
    this.onAIDynamicTokenInsert = callback;
  }
  handlePreviewNeedsReload() {
    const iframeWindow = window;

    // If the iframe window has react-dnd setup, we need
    // to make sure we teardown the react-dnd instances on both of those iframes.
    if (iframeWindow.__isReactDndBackendSetUp) {
      iframeWindow.__reactDndTeardownComplete = () => {
        this.reloadNow();
      };
      iframeWindow.postMessage({
        type: EVENT_REACT_DND_TEARDOWN_START
      }, '*');
    } else {
      this.reloadNow();
    }
  }
  reloadNow() {
    this.messenger.postMessage({
      action: 'refreshInpageFrame',
      scrollX: window.scrollX,
      scrollY: window.scrollY
    });
  }
  postMessage(typeOrMessage, dataOrCallback, callback) {
    // catch inline events here in form of object
    if (typeof typeOrMessage === 'object') {
      this.messenger.postMessage({
        action: 'inpage_message',
        data: typeOrMessage
      });
      return this.messenger.postMessage(typeOrMessage, dataOrCallback);
    } else if (typeOrMessage === UPLOAD_FILE) {
      // convert file to blob so it gets sent to frame correctly
      const blob = new Blob([dataOrCallback.file]);
      return this.messenger.postMessage({
        action: 'uploadFileEmailDnd',
        requestId: dataOrCallback.moduleId,
        fileAsBlob: blob,
        fileMeta: {
          name: dataOrCallback.file.name,
          lastModified: dataOrCallback.file.lastModified,
          type: dataOrCallback.file.type
        },
        moduleId: dataOrCallback.moduleId,
        validateImage: dataOrCallback.file.type.startsWith('image')
      }).then(callback);
    }
    return this.messenger.postMessage({
      action: 'inpage_message',
      type: typeOrMessage,
      data: dataOrCallback
    }, 'json').then(callback);
  }
}
export default Messenger;